// shared/src/schemas/signal.ts
// Zod schema for `signals` table — V3 unified event-horizon scoring contract.
//
// Source of truth: migrations/0028_event_horizon_reshape.sql (PaceDial brief
// revision 2026-05-27 — docs/short-horizon-pace-dial-2026-05-27-brief-revision.md).
//
// V3 reshape (from V2 migration 0018):
//   DROPPED: horizon_days, catalyst_date, catalyst_date_ci_low_days,
//            catalyst_date_ci_high_days, post_catalyst_window_days, path_type
//   RENAMED: conf_price → event_horizon_conf
//            inv_price  → event_horizon_inv
//   ADDED:   event_horizon_at (TEXT NOT NULL — ISO datetime), catalyst_label
//            (TEXT NOT NULL DEFAULT ''), max_excursion_high/low (REAL nullable),
//            event_horizon_outcome (enum nullable), event_horizon_outcome_price
//            (REAL nullable), event_horizon_outcome_at (TEXT nullable)
//   TIGHTENED: direction CHECK from ('long','short','flat') → ('long','short')
//              (0/35 PROD signals were flat; contract dead-weight removed)
//
// Total: 28 columns =
//   5 keys      (id · brief_id · ticker · signal_at · signal_date)
// + 18 scoring  (direction · magnitude · confidence · confidence_bucket ·
//                signal_price · event_horizon_conf · event_horizon_inv ·
//                pct_upside · pct_downside · rr_ratio · ev_pct ·
//                event_horizon_at · catalyst_label · max_excursion_high ·
//                max_excursion_low · event_horizon_outcome ·
//                event_horizon_outcome_price · event_horizon_outcome_at)
// + 2 metadata  (prediction_mode · timeframe · thesis_align)
// + 3 meta      (schema_version · created_at · details)
//
// A2 drift-detector (shared/__tests__/schema-vs-ddl.test.ts) replays the
// full migration ledger and enforces field-name parity with this schema.
//
// ABSOLUTE state-only rule (locked 2026-05-25 session 4): NO `action`
// column. The LLM emits PREDICTED STATE only; the trader decides actions.
//
// inv/conf SEMANTIC LOCK (PaceDial revision §2.4):
//   event_horizon_conf = price predicted to be REACHED/EXCEEDED by event_horizon_at
//   event_horizon_inv  = price predicted to be CLEARED by event_horizon_at
//     (or floor "minimum magnitude" when on same side as conf — one-sided CI)
// Neither is a live threshold; both are end-state predictions only.
// Pre-horizon trend tracking is server-side derived via PaceDial.

import { z } from 'zod';
import { coerceConfidenceBucket } from '../resolvers/llm-enum-coerce.js';

// D25 (2026-05-28): migration 0028's `event_horizon_at` backfill used
// SQLite `datetime(signal_at, '+N days')` which emits 'YYYY-MM-DD HH:MM:SS'
// (space separator, no Z suffix). Forward-emitted V3 rows are strict ISO
// 8601 with `T` separator and `Z` suffix. The Zod `.datetime()` validator
// rejects the SQLite shape outright. Preprocess normalizes the SQLite
// format to ISO at read-time so /api/signals serves V2-history mixed-shape
// rows without 500. The writer contract stays unchanged (chain still emits
// strict ISO); the migration backfill artifact is masked at the boundary.
function coerceDatetime(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v)) {
    return v.replace(' ', 'T') + 'Z';
  }
  return v;
}

// V3: 'flat' dropped from contract (PaceDial revision §2.3 + §3.1)
export const SignalDirectionEnum        = z.enum(['long', 'short']);
// 2026-05-29 — coerced so an LLM that emits "Medium"/"moderate"/"hi" etc. is
// translated to canonical HIGH/MED/LOW instead of hard-failing (the conviction
// fields on the 4 skeptic prompts + signals.confidence_bucket flow through
// this). Unknown values still reject (fail-loud). See llm-enum-coerce.ts.
export const SignalConfidenceBucketEnum = z.preprocess(coerceConfidenceBucket, z.enum(['HIGH', 'MED', 'LOW']));

export const SignalTimeframeEnum = z.enum(['1d', '5d', '1w', '1m', '1q', 'event-driven']);

export const SignalPredictionModeEnum = z.enum(['catalyst', 'trend']);

export const ThesisAlignEnum = z.enum(['ALIGNED', 'DIVERGENT', 'NEUTRAL']);

// NEW (V3): event_horizon_outcome — final scoring result, populated by the
// hourly scoring tick (cron-dispatcher) when event_horizon_at elapses.
export const EventHorizonOutcomeEnum = z.enum(['confirmed', 'invalidated', 'neither']);

export const SignalSchema = z.object({
  // ─── Keys ──────────────────────────────────────────────────────────────
  id:                          z.string(),                                  // UUIDv7
  brief_id:                    z.string(),
  ticker:                      z.string(),
  signal_at:                   z.string().datetime(),                       // ISO 8601
  signal_date:                 z.string(),                                  // 'YYYY-MM-DD' UTC

  // ─── Scoring contract (indexed; scorer + calibration query directly) ──
  direction:                   SignalDirectionEnum,
  magnitude:                   z.number(),                                  // signed % change at event horizon
  confidence:                  z.number().min(0).max(1),
  confidence_bucket:           SignalConfidenceBucketEnum,
  signal_price:                z.number().positive(),
  event_horizon_conf:          z.number().positive().nullable(),            // V3 rename: was conf_price
  event_horizon_inv:           z.number().positive().nullable(),            // V3 rename: was inv_price
  pct_upside:                  z.number().nullable(),                       // signed % from CI upper (display only)
  pct_downside:                z.number().nullable(),                       // signed % from CI lower (display only)
  rr_ratio:                    z.number().nonnegative().nullable(),
  ev_pct:                      z.number().nullable(),                       // confidence-weighted EV, signed

  // ─── Event horizon (V3 NEW — unified replaces horizon_days/catalyst_date/path_type) ──
  event_horizon_at:            z.preprocess(coerceDatetime, z.string().datetime()), // ISO 8601 (preprocess handles SQLite backfill format)
  catalyst_label:              z.string(),                                  // '' = synthetic 24h, else named event

  // ─── Pre-horizon tracking (V3 NEW — populated by refresh ticks) ───────
  max_excursion_high:          z.number().positive().nullable(),
  max_excursion_low:           z.number().positive().nullable(),

  // ─── Horizon scoring (V3 NEW — populated by hourly scoring tick) ──────
  event_horizon_outcome:       EventHorizonOutcomeEnum.nullable(),
  event_horizon_outcome_price: z.number().positive().nullable(),
  event_horizon_outcome_at:    z.string().datetime().nullable(),

  // ─── Retained metadata ─────────────────────────────────────────────────
  prediction_mode:             SignalPredictionModeEnum,                    // derivable from catalyst_label, kept for convenience
  timeframe:                   SignalTimeframeEnum,                         // bucket label (1w/1m/1q/etc) — derivable from event_horizon_at - signal_at
  thesis_align:                ThesisAlignEnum.nullable(),

  // ─── Meta ──────────────────────────────────────────────────────────────
  schema_version:              z.number().int(),                            // V3 = 3
  created_at:                  z.string().datetime(),
  details:                     z.string(),                                  // JSON sidecar; parsed by unmarshalSignalDetails
});

export type Signal = z.infer<typeof SignalSchema>;
